/**
 * parseMultiFileOutput — convert Claude's multi-file Markdown output into the
 * `FileSystemTree` shape expected by `@webcontainer/api`'s `mount()`.
 *
 * Supported input formats (Claude can pick either; we accept both):
 *
 *  Format A — fenced code blocks with a file path header:
 *      ```json
 *      // package.json
 *      { ... }
 *      ```
 *      ```jsx
 *      // src/App.jsx
 *      export default function App() { ... }
 *      ```
 *
 *  Format B — explicit FILE markers:
 *      ===FILE: package.json===
 *      { ... }
 *      ===FILE: src/App.jsx===
 *      export default function App() { ... }
 *
 * The function returns `null` if no multi-file block is detected, so the
 * caller can fall back to the legacy HTML mono-file flow without ambiguity.
 *
 * Tree shape (per WebContainer docs):
 *   { "src": { directory: { "App.jsx": { file: { contents: "..." } } } } }
 */

const FILE_MARKER_RE = /===FILE:\s*([^=\s][^=]*?)\s*===\s*\n([\s\S]*?)(?=\n===FILE:|\n*$)/g;
// Path header INSIDE the fence — accepts:
//   `// package.json`, `// path: package.json`, `// file: src/App.jsx`
//   `<!-- path: index.html -->`
//   `/* path: src/index.css */`
//   `# path: server.py`
const FENCED_RE = /```([a-zA-Z0-9_+\-./]*)\s*\n(?:\/\/|#|<!--|\/\*)\s*(?:path|file)?\s*:?\s*([\w./\-+]+\.[a-zA-Z0-9]+)[^\n]*\n([\s\S]*?)\n```/g;
// Path header OUTSIDE the fence (Claude often emits this form):
//   `// path: package.json`
//   ```json
//   {...}
//   ```
const PATH_THEN_FENCE_RE = /(?:^|\n)\s*(?:\/\/|#|<!--|\/\*)\s*(?:path|file)\s*:?\s*([\w./\-+]+\.[a-zA-Z0-9]+)[^\n]*\n+```[a-zA-Z0-9_+\-./]*\s*\n([\s\S]*?)\n```/g;
// Path-only separators with NO fences at all (Claude's most common output for
// multi-file projects — it sometimes drops the fences entirely):
//   // path: package.json
//   {...raw content...}
//   // path: vite.config.js
//   {...raw content...}
// Captures `path` from the marker and everything up to the NEXT path marker
// (or end-of-text).
const PATH_SEPARATED_RE = /(?:^|\n)\s*(?:\/\/|#|<!--|\/\*)\s*(?:path|file)\s*:?\s*([\w./\-+]+\.[a-zA-Z0-9]+)[^\n]*(?:-->|\*\/)?\s*\n([\s\S]*?)(?=\n\s*(?:\/\/|#|<!--|\/\*)\s*(?:path|file)\s*:?\s*[\w./\-+]+\.[a-zA-Z0-9]+|\s*$)/g;

export function parseMultiFileOutput(text) {
  if (!text || typeof text !== 'string') return null;

  const files = new Map();

  // --- Format B (===FILE:===) — preferred when both are present
  let m;
  FILE_MARKER_RE.lastIndex = 0;
  while ((m = FILE_MARKER_RE.exec(text)) !== null) {
    const path = m[1].trim();
    const contents = stripFenceWrapping(m[2]).trim();
    if (path && contents) files.set(path, contents);
  }

  // --- Format A (fenced ```...``` with path header) — only if B yielded nothing
  if (files.size === 0) {
    FENCED_RE.lastIndex = 0;
    while ((m = FENCED_RE.exec(text)) !== null) {
      const path = m[2].trim();
      const body = m[3];
      if (path && body && body.trim()) files.set(path, body);
    }
  }

  // --- Format C (path comment BEFORE the fence, Claude's preferred output)
  // Only if A yielded nothing OR for files A missed.
  PATH_THEN_FENCE_RE.lastIndex = 0;
  while ((m = PATH_THEN_FENCE_RE.exec(text)) !== null) {
    const path = m[1].trim();
    const body = m[2];
    if (path && body && body.trim() && !files.has(path)) {
      files.set(path, body);
    }
  }

  // --- Format D (path markers as plain separators, NO fences) — Claude's
  // actual production output. Captures everything between markers as the
  // file body, stripping wrapping HTML/CSS comment closers.
  if (files.size === 0) {
    PATH_SEPARATED_RE.lastIndex = 0;
    while ((m = PATH_SEPARATED_RE.exec(text)) !== null) {
      const path = m[1].trim();
      let body = (m[2] || '').trim();
      // Strip a stray closing comment from the previous line marker
      // (when path comes from <!-- path: x --> the regex sometimes leaves
      // a leading `-->` artifact; same for /* path: x */).
      body = body.replace(/^-->\s*\n?/, '').replace(/^\*\/\s*\n?/, '');
      if (path && body && !files.has(path)) {
        files.set(path, body);
      }
    }
  }

  if (files.size === 0) return null;

  // package.json must exist for `npm install`. If Claude forgot it, fail loud
  // — the parent will fall back to iframe srcDoc instead of starting a doomed
  // WebContainer boot.
  if (!files.has('package.json')) {
    return null;
  }

  // Auto-stub any internal relative import that points to a file Claude
  // forgot to emit. Prevents the "Failed to resolve import './pages/Tasks'"
  // red overlay — instead the missing route renders a friendly placeholder
  // so the user can still see the rest of the app.
  autofillMissingImports(files);

  // Safety net for bare-module imports Claude habitually slips in despite
  // the system-prompt ban (e.g. `prop-types`). If detected, ensure they're
  // in package.json deps so `npm install` resolves them instead of crashing
  // the preview with "Failed to resolve import 'prop-types'".
  ensureBareModuleDeps(files);

  // Convert flat Map<path, contents> to nested FileSystemTree
  return mapToTree(files);
}

// Bare-module imports Aria sometimes slips in despite the prompt ban.
// Map: package name → version range that ships safely with React 18.
const SAFETY_NET_DEPS = {
  'prop-types': '^15.8.1',
};
const BARE_IMPORT_RE = /(?:^|\n)\s*(?:import\s+(?:[\w*{},\s]+from\s+)?|export\s+(?:[\w*{},\s]+from\s+))['"]([^'".\/][^'"]*)['"]/g;

function ensureBareModuleDeps(files) {
  const pkgRaw = files.get('package.json');
  if (!pkgRaw) return;
  let pkg;
  try { pkg = JSON.parse(pkgRaw); } catch (e) { return; }
  if (!pkg || typeof pkg !== 'object') return;
  pkg.dependencies = pkg.dependencies || {};

  const seen = new Set();
  for (const [path, text] of files.entries()) {
    if (!/\.(jsx?|tsx?|mjs|cjs)$/.test(path)) continue;
    BARE_IMPORT_RE.lastIndex = 0;
    let m;
    while ((m = BARE_IMPORT_RE.exec(text)) !== null) {
      // Take the package name root (handles `pkg/sub/path` and `@scope/pkg/sub`)
      const spec = m[1];
      const root = spec.startsWith('@')
        ? spec.split('/').slice(0, 2).join('/')
        : spec.split('/')[0];
      seen.add(root);
    }
  }

  let mutated = false;
  for (const dep of seen) {
    if (SAFETY_NET_DEPS[dep] && !pkg.dependencies[dep] && !(pkg.devDependencies || {})[dep]) {
      pkg.dependencies[dep] = SAFETY_NET_DEPS[dep];
      mutated = true;
    }
  }
  if (mutated) {
    files.set('package.json', JSON.stringify(pkg, null, 2));
  }
}

const RELATIVE_IMPORT_RE = /(?:^|\n)\s*(?:import\s+(?:[\w*{},\s]+from\s+)?|export\s+(?:[\w*{},\s]+from\s+))['"]([./][^'"]+)['"]/g;

function autofillMissingImports(files) {
  const RESOLVE_EXTS = ['', '.jsx', '.tsx', '.js', '.ts', '/index.jsx', '/index.tsx', '/index.js', '/index.ts'];
  const sources = Array.from(files.keys()).filter(p => /\.(jsx?|tsx?|mjs|cjs)$/.test(p));
  const visited = new Set();
  // Loop until no new stubs are added (a stub may itself reference imports,
  // but we keep stubs import-free, so this terminates in one pass).
  let added = true;
  let safetyHops = 5;
  while (added && safetyHops-- > 0) {
    added = false;
    for (const src of sources) {
      if (visited.has(src)) continue;
      visited.add(src);
      const text = files.get(src) || '';
      RELATIVE_IMPORT_RE.lastIndex = 0;
      let m;
      while ((m = RELATIVE_IMPORT_RE.exec(text)) !== null) {
        const spec = m[1];
        const target = resolveRelative(src, spec);
        if (!target) continue;
        const resolved = RESOLVE_EXTS.map(ext => target + ext).find(p => files.has(p));
        if (resolved) continue;
        // Pick a default extension based on the importer
        const ext = src.match(/\.(tsx)$/) ? '.tsx' : '.jsx';
        const stubPath = target + ext;
        if (files.has(stubPath)) continue;
        files.set(stubPath, makeStub(stubPath));
        added = true;
      }
    }
  }
}

function resolveRelative(fromPath, spec) {
  if (!spec.startsWith('./') && !spec.startsWith('../')) return null;
  const fromDir = fromPath.split('/').slice(0, -1);
  const parts = spec.split('/');
  const out = [...fromDir];
  for (const p of parts) {
    if (p === '.' || p === '') continue;
    if (p === '..') out.pop();
    else out.push(p);
  }
  return out.join('/');
}

function makeStub(path) {
  const name = (path.split('/').pop() || 'Component').replace(/\.(jsx?|tsx?)$/, '');
  const isReact = /\.(jsx|tsx)$/.test(path);
  if (!isReact) {
    return `// Auto-generated stub for missing file: ${path}\n// (Aria referenced this path but did not emit the file.)\nexport default {};\n`;
  }
  return `// Auto-generated stub for missing file: ${path}
// (Aria referenced this path but did not emit the file. The preview keeps
// running so you can ship a follow-up message asking Aria to fill it in.)
import React from 'react';

export default function ${name}() {
  return (
    <div style={{
      padding: 40,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      minHeight: 320,
      borderRadius: 14,
      background: 'rgba(0,201,167,0.04)',
      border: '1px dashed rgba(0,201,167,0.35)',
      color: '#1f2937',
      fontFamily: 'system-ui, sans-serif',
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#0d9488' }}>
        Missing file · auto-stub
      </div>
      <div style={{ fontSize: 18, fontWeight: 600 }}>${name}</div>
      <div style={{ fontSize: 13, color: '#475569', maxWidth: 480, textAlign: 'center', lineHeight: 1.55 }}>
        Aria referenced <code>${path}</code> but did not emit it. Ask Aria in the chat:
        <em>"Add the missing ${name} page with full content."</em>
      </div>
    </div>
  );
}
`;
}

function stripFenceWrapping(s) {
  // If Claude wrapped contents inside ```lang … ```, drop the fence.
  const trimmed = s.trim();
  if (trimmed.startsWith('```')) {
    return trimmed
      .replace(/^```[a-zA-Z0-9_+\-./]*\s*\n/, '')
      .replace(/\n```\s*$/, '');
  }
  return s;
}

function mapToTree(files) {
  const tree = {};
  for (const [path, contents] of files.entries()) {
    const parts = path.split('/').filter(Boolean);
    let node = tree;
    for (let i = 0; i < parts.length - 1; i++) {
      const seg = parts[i];
      if (!node[seg]) node[seg] = { directory: {} };
      else if (!node[seg].directory) node[seg] = { directory: {} };
      node = node[seg].directory;
    }
    const fileName = parts[parts.length - 1];
    node[fileName] = { file: { contents } };
  }
  return tree;
}

/**
 * Heuristic to decide if we should even try the multi-file runtime. Cheap
 * scan that runs before the expensive parse.
 */
export function hasMultiFileMarkers(text) {
  if (!text) return false;
  return /===FILE:\s*[\w./\-+]+\s*===/.test(text)
      || /```[a-z]*\s*\n\s*(?:\/\/|#|<!--|\/\*)\s*(?:path|file)?\s*:?\s*package\.json/i.test(text)
      || /(?:\/\/|#|<!--|\/\*)\s*(?:path|file)\s*:?\s*package\.json/i.test(text);
}
